import { useState, useEffect, useCallback } from 'react';
import Modal from '../common/Modal';
import Spinner from '../common/Spinner';
import { searchUsers } from '../../api/admin';
import { useImpersonation } from '../../hooks/useImpersonation';
import type { AdminSearchUser } from '../../types/impersonation';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function ImpersonationSearchModal({ isOpen, onClose }: Props) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<AdminSearchUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { startImpersonation } = useImpersonation();

  // Debounced search. Short queries are cleared in the input onChange handler,
  // so this effect only ever schedules the async fetch.
  useEffect(() => {
    if (query.length < 2) return;

    const timer = setTimeout(async () => {
      setIsLoading(true);
      setError(null);
      try {
        const users = await searchUsers(query);
        setResults(users);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Search failed');
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  // Reset state whenever the modal is dismissed (event-driven, replacing the
  // previous reset-on-close effect).
  const handleClose = useCallback(() => {
    setQuery('');
    setResults([]);
    setError(null);
    onClose();
  }, [onClose]);

  const handleSelect = useCallback(
    (user: AdminSearchUser) => {
      startImpersonation({
        id: user.id,
        email: user.email,
        organization_name: user.organization_name,
      });
      handleClose();
    },
    [startImpersonation, handleClose]
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Impersonate User">
      <div className="space-y-4">
        {/* Search Input */}
        <div className="relative">
          <input
            type="text"
            value={query}
            onChange={(e) => {
              const value = e.target.value;
              setQuery(value);
              if (value.length < 2) {
                setResults([]);
                setError(null);
              }
            }}
            placeholder="Search by email or name..."
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            autoFocus
          />
          {isLoading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <Spinner />
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <p className="text-sm text-red-600">{error}</p>
        )}

        {/* Results */}
        {results.length > 0 && (
          <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-200">
            {results.map((user) => (
              <button
                key={user.id}
                onClick={() => handleSelect(user)}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors"
              >
                <div className="font-medium text-gray-900">{user.email}</div>
                <div className="text-sm text-gray-500">
                  {user.organization_name || 'No organization'}
                  {' - '}
                  {user.role}
                  {user.subscription_count !== undefined && (
                    <span> - {user.subscription_count} subscription{user.subscription_count !== 1 ? 's' : ''}</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Empty State */}
        {query.length >= 2 && !isLoading && results.length === 0 && !error && (
          <p className="text-sm text-gray-500 text-center py-4">
            No users found matching "{query}"
          </p>
        )}

        {/* Hint */}
        {query.length < 2 && (
          <p className="text-sm text-gray-500 text-center py-4">
            Enter at least 2 characters to search
          </p>
        )}
      </div>
    </Modal>
  );
}
